import * as React from 'react';

import { Slot } from '@radix-ui/react-slot';
import { linkVariants, type LinkVariantProps } from './link.variants';
import { cn } from '@/hen-ui/utils';

export interface LinkProps
  extends React.AnchorHTMLAttributes<HTMLAnchorElement>,
    LinkVariantProps {
  asChild?: boolean;
}

/**
 * Link component following HEN Design System standards.
 *
 * A semantic anchor element with HEN-specific variants and design tokens.
 * All styling uses tokens from tailwind.config.ts.
 *
 * @example
 * // Basic usage
 * <Link href="/about">About Us</Link>
 *
 * @example
 * // With HEN variant and size
 * <Link variant="primary" size="lg" href="/contact">
 *   Contact Us
 * </Link>
 *
 * @example
 * // With underline control
 * <Link variant="subtle" underline="always" href="/terms">
 *   Terms & Conditions
 * </Link>
 *
 * @example
 * // With icons (import from lucide-react)
 * import { ExternalLink } from 'lucide-react';
 * <Link variant="primary" href="https://example.com" target="_blank">
 *   Visit Site <ExternalLink />
 * </Link>
 *
 * @example
 * // External link with security best practices
 * <Link href="https://example.com" target="_blank" rel="noopener noreferrer">
 *   External Link
 * </Link>
 */
export const Link = React.forwardRef<HTMLAnchorElement, LinkProps>(
  (
    { className, variant, size, underline, children, asChild, ...props },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'a';
    return (
      <Comp
        className={cn(linkVariants({ variant, size, underline }), className)}
        ref={ref}
        {...props}
      >
        {children}
      </Comp>
    );
  },
);

Link.displayName = 'Link';
